import { useEffect, useRef, useState, type RefObject } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";

export interface VideoChapter {
  time: string;
  title: string;
}

interface VideoChaptersProps {
  /** Ref to the video element. When omitted, the nearest preceding <video> in the document is used. */
  videoRef?: RefObject<HTMLVideoElement | null>;
  chapters: VideoChapter[];
  /** Read ?t= / #t= on mount and seek the video to that moment. */
  enableDeepLink?: boolean;
  className?: string;
}

export const parseTimestamp = (t: string): number | null => {
  const s = t.trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (!/^\d{1,2}(:\d{2}){1,2}$/.test(s)) return null;
  return s.split(":").reduce((acc, p) => acc * 60 + parseInt(p, 10), 0);
};

const findPrecedingVideo = (el: HTMLElement | null): HTMLVideoElement | null => {
  if (!el) return null;
  const videos = Array.from(document.querySelectorAll("video"));
  let found: HTMLVideoElement | null = null;
  for (const v of videos) {
    if (v.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) found = v;
  }
  return found;
};

const scrollIntoViewIfNeeded = (v: HTMLElement) => {
  const r = v.getBoundingClientRect();
  if (r.top < 0 || r.bottom > window.innerHeight) {
    v.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

const VideoChapters = ({ videoRef, chapters, enableDeepLink = true, className }: VideoChaptersProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number | null>(null);

  const getVideo = () => videoRef?.current ?? findPrecedingVideo(listRef.current);

  useEffect(() => {
    const video = getVideo();
    if (!video) return;
    const starts = chapters.map((c) => parseTimestamp(c.time) ?? 0);
    const onTime = () => {
      const t = video.currentTime;
      if (t === 0 && video.paused) return setActive(null);
      let idx = -1;
      starts.forEach((s, i) => { if (t >= s) idx = i; });
      setActive(idx >= 0 ? idx : null);
    };
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("seeked", onTime);

    if (enableDeepLink) {
      const params = new URLSearchParams(window.location.search);
      const hash = window.location.hash.match(/[#&]t=([\d:]+)/);
      const raw = params.get("t") ?? hash?.[1] ?? null;
      const secs = raw ? parseTimestamp(raw) : null;
      if (secs !== null) {
        const go = () => {
          video.currentTime = secs;
          scrollIntoViewIfNeeded(video);
          video.play().catch(() => {});
        };
        if (video.readyState >= 1) go();
        else video.addEventListener("loadedmetadata", go, { once: true });
      }
    }
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("seeked", onTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters, enableDeepLink]);

  const seek = (time: string) => {
    const secs = parseTimestamp(time);
    const video = getVideo();
    if (secs === null || !video) return;
    video.currentTime = secs;
    video.play().catch(() => {});
    scrollIntoViewIfNeeded(video);
  };

  return (
    <div ref={listRef} className={cn("not-prose my-8 rounded-2xl bg-muted/40 border border-border/50 overflow-hidden", className)}>
      <ol style={{ listStyle: "none", margin: 0, padding: 0 }} className="divide-y divide-border/50">
        {chapters.map((c, i) => {
          const isActive = active === i;
          return (
            <li key={`${c.time}-${i}`}>
              <button
                type="button"
                onClick={() => seek(c.time)}
                aria-label={`Jump to ${c.time}: ${c.title}`}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "group grid w-full min-h-[44px] grid-cols-[5.25rem_1fr] items-start gap-4 px-4 sm:px-5 py-3.5 text-left cursor-pointer transition-colors hover:bg-primary/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                  isActive && "bg-primary/[0.07]"
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 inline-flex w-full items-center justify-center gap-1.5 rounded-full border px-2 py-1 font-mono text-xs tabular-nums transition-colors",
                    isActive
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-accent/30 bg-background text-accent group-hover:border-accent group-hover:bg-accent/10"
                  )}
                >
                  <Play size={10} className="fill-current shrink-0" aria-hidden="true" />
                  {c.time}
                </span>
                <span
                  className={cn(
                    "text-[0.9375rem] leading-relaxed text-foreground/80 group-hover:text-foreground",
                    isActive ? "font-semibold text-foreground" : "font-normal"
                  )}
                >
                  {c.title}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default VideoChapters;
